/**
 * Shared renderer for every /legal/* screen. Looks up the requested
 * doc from @tago/legal, converts to markdown, renders via MarkdownView
 * inside the standard ScreenContainer + back button + scroll layout.
 *
 * The page-level files (terms-of-service.tsx etc.) are tiny shims that
 * just pass the slug, keeping Expo Router's file-based routing happy
 * while the actual rendering lives here for DRY.
 */
import { View, Text, ScrollView, Linking } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import {
  getLegalContent,
  getLegalContentVersion,
  toMarkdown,
  type LegalSlug,
  type LegalLocale,
} from '@tago/legal'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { MarkdownView } from '@/components/ui/MarkdownView'
import { Button } from '@/components/ui/Button'
import { GlassSheet } from '@/components/ui/GlassSheet'
import { useCurrentRentalTerms } from '@/lib/hooks/use-rental-terms'
import { toast } from '@/lib/toast'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING } from '@/lib/theme/tokens'
import { FONTS } from '@/lib/brand'

interface LegalScreenProps {
  slug: LegalSlug
  /** Signup records the frozen account ToS v1.3; rental consent is separate. */
  frozenAccountTerms?: boolean
}

export function LegalScreen({ slug, frozenAccountTerms = false }: LegalScreenProps) {
  const { i18n, t } = useTranslation()
  const insets = useSafeAreaInsets()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { data: currentTerms, isLoading, refetch } = useCurrentRentalTerms()

  const locale: LegalLocale = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'es'
  const isRentalDocument = slug === 'terms-of-service' || slug === 'cancellation-policy'
  const title =
    slug === 'terms-of-service'
      ? t('rental_terms_flow.read_terms')
      : t('rental_terms_flow.read_cancellation')
  if (
    isRentalDocument &&
    !frozenAccountTerms &&
    (!currentTerms || currentTerms.version !== '1.3')
  ) {
    const documentUrl =
      slug === 'terms-of-service' ? currentTerms?.termsUrl : currentTerms?.cancellationUrl
    const openCurrentDocument = async () => {
      if (!documentUrl) return
      try {
        await Linking.openURL(documentUrl)
      } catch {
        toast.error(t('rental_terms_flow.open_error'))
      }
    }
    return (
      <ScreenContainer scroll={false}>
        <ScreenHeader backTestID={`legal.${slug}.back`} />
        <View style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.lg }}>
          <Text style={{ color: colors.textPrimary, fontFamily: FONTS.bodyBold, fontSize: 26 }}>
            {title}
          </Text>
          <GlassSheet
            contentStyle={{ marginTop: SPACING.lg, padding: SPACING.lg, gap: SPACING.md }}
          >
            <Text
              style={{
                color: colors.textSecondary,
                fontFamily: FONTS.body,
                fontSize: 15,
                lineHeight: 22,
              }}
            >
              {currentTerms
                ? t('rental_terms_flow.profile_current_version', { version: currentTerms.version })
                : t(isLoading ? 'rental_terms_flow.loading' : 'rental_terms_flow.unavailable')}
            </Text>
            {currentTerms ? (
              <Button
                title={t('rental_terms_flow.open_current_document')}
                onPress={() => void openCurrentDocument()}
                testID={`legal.${slug}.open-current`}
              />
            ) : !isLoading ? (
              <Button
                title={t('rental_terms_flow.retry')}
                variant="secondary"
                onPress={() => void refetch()}
                testID={`legal.${slug}.retry`}
              />
            ) : null}
          </GlassSheet>
        </View>
      </ScreenContainer>
    )
  }
  const content = frozenAccountTerms
    ? getLegalContentVersion(slug, locale, '1.3')
    : getLegalContent(slug, locale)
  if (!content) throw new Error('The archived account terms are unavailable')
  const markdown = toMarkdown(content)

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader backTestID={`legal.${slug}.back`} />

      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: SPACING.lg,
          paddingBottom: insets.bottom + SPACING.xl,
        }}
        showsVerticalScrollIndicator={false}
      >
        <Text
          style={{
            fontSize: 12,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            fontFamily: FONTS.bodyBold,
            color: colors.textMuted,
          }}
        >
          {content.kicker}
        </Text>
        <Text
          style={{
            fontSize: 28,
            lineHeight: 34,
            letterSpacing: -0.5,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
            marginTop: SPACING.xs,
          }}
        >
          {content.title}
        </Text>
        {content.effectiveDate ? (
          <Text
            style={{
              fontSize: 13,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              marginTop: SPACING.sm,
            }}
          >
            {content.effectiveDate}
          </Text>
        ) : null}

        <View style={{ marginTop: SPACING.xl, maxWidth: 720, width: '100%' }}>
          <MarkdownView source={markdown} />
        </View>
      </ScrollView>
    </ScreenContainer>
  )
}
